import { css } from '@emotion/react';
import React, { useState, useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from 'react-bootstrap';
import t from 'react-translate';
import NvModal, { ModalType } from 'shared/components/nv-modal';
import { lightFontWeight, boldFontWeight, textMediumFontSize } from 'styles/global_defaults/fonts';
import { primary, gray3, gray5, white } from 'styles/global_defaults/colors';
import { halfSpacing, standardSpacing, standardModalWidth, tripleSpacing } from 'styles/global_defaults/scaffolding';
import { LearnerProgressFilters } from './filters-flyout-modal';
import useContactLearners from './use-contact-learners';
import AudienceSummary from './contact-learners/audience-summary';
import StepWriteMessage from './contact-learners/step-write-message';
import StepPreview from './contact-learners/step-preview';
import {
  ContactLearnersFormValues,
  buildDefaultValues,
  buildValidationSchema,
} from './contact-learners/contact-learners-form';
import getDefaultMessageVariant from './contact-learners/get-default-message-variant';

type Step = 'write' | 'preview';

type ContactLearnersModalProps = {
  show: boolean;
  onClose: () => void;
  audienceLabel: string;
  activeTab: string;
  activeFilters: LearnerProgressFilters | null;
};

const stepIndicatorStyles = css`
  &.step-indicator {
    padding-bottom: ${standardSpacing}px;

    .step-dots {
      gap: ${halfSpacing}px;
      margin-bottom: ${halfSpacing}px;

      .step-dot {
        width: 28px;
        height: 28px;
        border-radius: 50%;
        font-weight: ${boldFontWeight};
        font-size: ${textMediumFontSize}px;
        background-color: ${gray3};
        color: ${white};
        border: none;

        &.active {
          background-color: ${primary};
        }
      }
    }

    .step-title {
      font-size: 24px;
      font-weight: ${lightFontWeight};
    }

    .step-divider {
      height: 1px;
      background-color: ${gray5};
      margin-top: ${halfSpacing}px;
    }
  }
`;

const bodyStyles = css`
  padding: ${standardSpacing}px ${tripleSpacing}px;
`;

const ContactLearnersModal = ({
  show,
  onClose,
  audienceLabel,
  activeTab,
  activeFilters,
}: ContactLearnersModalProps) => {
  const [step, setStep] = useState<Step>('write');
  const [isSending, setIsSending] = useState(false);
  // Frozen at the closed → open transition, so a tab/filter change underneath the open
  // modal can't redirect the send to a different audience than the one the user started from.
  const [audienceContext, setAudienceContext] = useState({ activeTab, activeFilters, audienceLabel });

  const { sendContactEmail } = useContactLearners({
    activeTab: audienceContext.activeTab,
    activeFilters: audienceContext.activeFilters,
  });

  const rhf = useForm<ContactLearnersFormValues>({
    mode: 'all',
    resolver: yupResolver(buildValidationSchema()),
    defaultValues: buildDefaultValues(getDefaultMessageVariant(activeTab)),
  });

  const { handleSubmit, formState, reset } = rhf;

  const wasShown = useRef(show);

  // Reset to fresh defaults only on the closed → open transition, so a tab change
  // underneath the open modal can't wipe what the user is composing.
  useEffect(() => {
    if (show && !wasShown.current) {
      setAudienceContext({ activeTab, activeFilters, audienceLabel });
      reset(buildDefaultValues(getDefaultMessageVariant(activeTab)));
      setStep('write');
    }
    wasShown.current = show;
  }, [show, activeTab, activeFilters, audienceLabel, reset]);

  const handleClose = () => {
    onClose();
    reset(buildDefaultValues(getDefaultMessageVariant(activeTab)));
    setStep('write');
  };

  const handleNext = handleSubmit(() => {
    setStep('preview');
  });

  const handleBack = () => {
    setStep('write');
  };

  const handleSend = handleSubmit(async (values) => {
    setIsSending(true);
    // sendContactEmail (the thunk) already surfaces success/error toasts — don't double-toast here.
    const sent = await sendContactEmail(
      { subject: values.subject, body: values.body },
      values.includePushNotification
        ? { enablePushNotification: true, pushNotificationBody: values.pushNotificationBody }
        : undefined,
    ).then(() => true).catch(() => false);
    setIsSending(false);
    if (sent) handleClose();
  });

  const stepTitle = step === 'write'
    ? t.LEARNER_PROGRESS.CONTACT_BAR.MODAL.STEP_WRITE()
    : t.LEARNER_PROGRESS.CONTACT_BAR.MODAL.STEP_PREVIEW();

  const stepBody = (
    <FormProvider {...rhf}>
      <div css={bodyStyles}>
        <div css={stepIndicatorStyles} className='step-indicator d-flex flex-column align-items-center'>
          <div className='step-dots d-flex' role='list' aria-label={t.LEARNER_PROGRESS.CONTACT_BAR.MODAL.STEPS_LABEL()}>
            <div
              className={`step-dot d-flex align-items-center justify-content-center ${step === 'write' ? 'active' : ''}`}
              role='listitem'
              aria-current={step === 'write' ? 'step' : undefined}
            >
              {t.LEARNER_PROGRESS.CONTACT_BAR.MODAL.STEP_ONE_NUMBER()}
            </div>
            <div
              className={`step-dot d-flex align-items-center justify-content-center ${step === 'preview' ? 'active' : ''}`}
              role='listitem'
              aria-current={step === 'preview' ? 'step' : undefined}
            >
              {t.LEARNER_PROGRESS.CONTACT_BAR.MODAL.STEP_TWO_NUMBER()}
            </div>
          </div>
          <div className='step-title'>{stepTitle}</div>
          <div className='step-divider w-100' aria-hidden='true' />
        </div>
        <AudienceSummary audienceLabel={audienceContext.audienceLabel} />
        {step === 'write' && <StepWriteMessage />}
        {step === 'preview' && <StepPreview />}
      </div>
    </FormProvider>
  );

  const stepFooter = (
    <div className='d-flex justify-content-center w-100'>
      {step === 'write' ? (
        <Button
          type='button'
          onClick={handleNext}
          disabled={!formState.isValid}
        >
          {t.LEARNER_PROGRESS.CONTACT_BAR.MODAL.NEXT()}
        </Button>
      ) : (
        <>
          <Button
            variant='secondary'
            type='button'
            className='mr-2'
            onClick={handleBack}
          >
            {t.FORM.BACK()}
          </Button>
          <Button
            type='button'
            onClick={handleSend}
            disabled={isSending || !formState.isValid}
          >
            {t.LEARNER_PROGRESS.CONTACT_BAR.MODAL.SEND()}
          </Button>
        </>
      )}
    </div>
  );

  return (
    <NvModal
      type={ModalType.DYNAMIC}
      width={standardModalWidth}
      header={t.LEARNER_PROGRESS.CONTACT_BAR.MODAL.TITLE()}
      show={show}
      onClose={isSending ? undefined : handleClose}
      bodyClassName='px-0'
      body={stepBody}
      footer={stepFooter}
    />
  );
};

export default ContactLearnersModal;
